import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import { Form } from '@ant-design/compatible';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import { Select, DatePicker, InputNumber, Row, Col, Tooltip, Input, Checkbox } from 'antd';

import '@ant-design/compatible/assets/index.css';
import TenancyDetailsPreview from '~components/tenancy-details/preview';
import { getTenancyFieldsConfig } from 'common/helpers/listing-edit';

const Option = Select.Option;

export default class TenancyDetails extends React.Component {
  constructor(props) {
    super(props);
    const { billingCycle } = props;
    this.billingCycleRange = billingCycle === 'WEEKLY' ? 7 : 14;
    this.state = {
      moveOutDateRange: {
        startValue: null,
        endValue: null,
      },
    };
  }

  handleMoveInSelectChange = value => {
    if (value === 'ANYTIME') {
      this.props.form.resetFields(['moveIn', 'moveOut', 'moveOutType']);
    }

    if (value !== 'EXACTLY_MATCH') {
      this.props.form.resetFields(['availableThoughMoveInDatePassed']);
    }

    this.handleCalculateMoveOutDateRange('moveInType', value);
  };

  handleMoveInDatePicker = date => {
    this.handleCalculateMoveOutDateRange('moveIn', date);
  };

  handleTenancyLengthSelectChange = value => {
    if (value === 'NOT_SPECIFIC') {
      this.props.form.resetFields(['tenancyLengthValueMin', 'tenancyLengthValueMax']);
    }
    this.handleCalculateMoveOutDateRange('tenancyLengthType', value);
    this.setState(this.state);
  };

  handleTenancyLengthMinInputChange = value => {
    this.handleCalculateMoveOutDateRange('tenancyLengthValueMin', value);
  };

  handleTenancyLengthMaxInputChange = value => {
    this.handleCalculateMoveOutDateRange('tenancyLengthValueMax', value);
    this.props.form.validateFields(['tenancyLengthValueMin']);
  };

  handleCalculateMoveOutDateRange = (type, value, fieldsState = false) => {
    let moveInType = this.props.form.getFieldValue('moveInType');
    let moveIn = this.props.form.getFieldValue('moveIn');
    let tenancyLengthType = this.props.form.getFieldValue('tenancyLengthType');
    let tenancyLengthValueMin = this.props.form.getFieldValue('tenancyLengthValueMin');
    let tenancyLengthValueMax = this.props.form.getFieldValue('tenancyLengthValueMax');
    let resetFields = ['moveOut'];

    if (fieldsState) {
      resetFields = [];
    }

    switch (type) {
      case 'moveInType':
        moveInType = value;
        resetFields.push('moveOutType');
        break;
      case 'moveIn':
        moveIn = value;
        break;
      case 'tenancyLengthType':
        tenancyLengthType = value;
        resetFields.push('moveOutType');
        break;
      case 'tenancyLengthValueMin':
        tenancyLengthValueMin = value;
        break;
      case 'tenancyLengthValueMax':
        tenancyLengthValueMax = value;
        break;
      default:
        break;
    }

    if (moveInType === 'ANYTIME') {
      moveIn = moment();
    }

    if (
      !moveIn ||
      (!tenancyLengthValueMin && tenancyLengthType !== 'NOT_SPECIFIC') ||
      !moveInType ||
      !tenancyLengthType ||
      tenancyLengthValueMin === 0 ||
      (tenancyLengthType === 'BETWEEN' && !tenancyLengthValueMax)
    ) {
      this.props.form.resetFields(resetFields);
      return true;
    }

    if (moveInType === 'ANYTIME') {
      resetFields.push('moveIn');
    }
    if (tenancyLengthType !== 'BETWEEN') {
      resetFields.push('tenancyLengthValueMax');
    }

    this.props.form.resetFields(resetFields);

    switch (moveInType) {
      case 'EXACTLY_MATCH':
        if (tenancyLengthType === 'EQUALS') {
          this.setMoveInExactlyMatchEqual(moveIn, tenancyLengthValueMin, fieldsState);
        }
        if (tenancyLengthType === 'NO_LESS_THAN') {
          this.setMoveInExactlyMatchBigger(moveIn, tenancyLengthValueMin);
        }
        if (tenancyLengthType === 'NO_MORE_THAN') {
          this.setMoveInExactlyMatchSmaller(moveIn, tenancyLengthValueMin);
        }
        if (tenancyLengthType === 'BETWEEN') {
          this.setMoveInExactlyMatchBetween(moveIn, tenancyLengthValueMin, tenancyLengthValueMax);
        }
        if (tenancyLengthType === 'NOT_SPECIFIC') {
          this.setMoveInAfterSmaller(moveIn);
        }
        break;
      case 'AFTER':
        if (['NO_LESS_THAN', 'EQUALS'].indexOf(tenancyLengthType) !== -1) {
          this.setMoveInExactlyMatchBigger(moveIn, tenancyLengthValueMin);
        }
        if (tenancyLengthType === 'NOT_SPECIFIC') {
          this.setMoveInAfterSmaller(moveIn);
        }
        if (tenancyLengthType === 'NO_MORE_THAN') {
          this.setMoveInAfterNoMoreThen(moveIn, tenancyLengthValueMin);
        }
        if (tenancyLengthType === 'BETWEEN') {
          this.setMoveInAfterBetween(moveIn, tenancyLengthValueMin);
        }
        break;
      case 'ANYTIME':
        if (['NO_LESS_THAN', 'EQUALS'].indexOf(tenancyLengthType) !== -1) {
          this.setMoveInExactlyMatchBigger(moment(), tenancyLengthValueMin);
        }
        if (tenancyLengthType === 'NOT_SPECIFIC') {
          this.setMoveInAfterSmaller(moment());
        }
        if (tenancyLengthType === 'NO_MORE_THAN') {
          this.setMoveInAnytimeNoMoreThen();
        }
        if (tenancyLengthType === 'BETWEEN') {
          this.setMoveInAnytimeBetween(tenancyLengthValueMin);
        }
        break;
      default:
        break;
    }

    return true;
  };

  // if range start date before current, then set start date equals curent day
  setMoveOutDateRange(startValue, endValue, moveInValue) {
    this.state.moveOutDateRange.startValue = null;
    this.state.moveOutDateRange.endValue = null;

    if (startValue) {
      const currentDay = moment().add(1, 'days');
      const moveInDay = moveInValue ? moment(moveInValue).add(1, 'days') : currentDay;
      this.state.moveOutDateRange.startValue = moment.max(startValue, currentDay, moveInDay);
    }
    if (endValue) {
      this.state.moveOutDateRange.endValue = endValue;
    }
  }

  // this.state.moveOutDateRange = {
  //   startValue: 'a+b-7',
  //   endValue: 'a+b+7',
  // }
  setMoveInExactlyMatchEqual = (moveIn, tenancyLengthValue, fieldsState) => {
    const moveOut = moment(moveIn).add(this.getTenancyLengthToDays(tenancyLengthValue), 'days');
    if (!fieldsState) {
      this.props.form.setFieldsValue({
        moveOutType: 'EXACTLY_MATCH',
        moveOut,
      });
    }

    this.props.form.validateFields(['moveOut']);

    this.setMoveOutDateRange(
      moment(moveIn).add(
        this.getTenancyLengthToDays(tenancyLengthValue) - this.billingCycleRange,
        'days',
      ),
      moment(moveIn).add(
        this.getTenancyLengthToDays(tenancyLengthValue) + this.billingCycleRange,
        'days',
      ),
      moveIn,
    );
  };

  // this.state.moveOutDateRange = {
  //   startValue: 'a+b-7',
  //   endValue: 'null',
  // }
  setMoveInExactlyMatchBigger = (moveIn, tenancyLengthValue) => {
    this.setMoveOutDateRange(
      moment(moveIn).add(this.getTenancyLengthToDays(tenancyLengthValue), 'days'),
      null,
      moveIn,
    );
  };

  // this.state.moveOutDateRange = {
  //   startValue: 'a+b-7',
  //   endValue: 'a+b+7',
  // }
  setMoveInExactlyMatchSmaller = (moveIn, tenancyLengthValue) => {
    this.setMoveOutDateRange(
      moment(moveIn).add(1, 'days'),
      moment(moveIn).add(this.getTenancyLengthToDays(tenancyLengthValue), 'days'),
      moveIn,
    );
  };

  // this.state.moveOutDateRange = {
  //   startValue: 'a',
  //   endValue: 'null',
  // }
  setMoveInAfterSmaller = moveIn => {
    this.setMoveOutDateRange(moveIn, null, moveIn);
  };

  // this.setMoveInAfterNoMoreThen = {
  //   startValue: 'a+b-7',
  //   endValue: 'null',
  // }
  setMoveInAfterNoMoreThen = moveIn => {
    this.setMoveOutDateRange(moment(moveIn).add(1, 'days'), null, moveIn);
  };

  // this.setMoveInAfterBetween = {
  //   startValue: 'a+b'-7',
  //   endValue: 'null',
  // }
  setMoveInAfterBetween = (moveIn, tenancyLengthValue) => {
    this.setMoveOutDateRange(
      moment(moveIn).add(this.getTenancyLengthToDays(tenancyLengthValue), 'days'),
      null,
      moveIn,
    );
  };

  // this.setMoveInAnytimeNoMoreThen = {
  //   startValue: '-7',
  //   endValue: 'null',
  // }
  setMoveInAnytimeNoMoreThen = () => {
    this.setMoveOutDateRange(moment().add(-this.billingCycleRange, 'days'));
  };

  // this.setMoveInAnytimeBetween = {
  //   startValue: 'b'-7',
  //   endValue: 'null',
  // }
  setMoveInAnytimeBetween = tenancyLengthValue => {
    this.setMoveOutDateRange(moment().add(this.getTenancyLengthToDays(tenancyLengthValue), 'days'));
  };

  // this.state.moveOutDateRange = {
  //   startValue: 'a + b' - 7d',
  //   endValue: 'a + b' + 7d',
  // }
  setMoveInExactlyMatchBetween = (moveIn, tenancyLengthValueMin, tenancyLengthValueMax) => {
    this.setMoveOutDateRange(
      moment(moveIn).add(this.getTenancyLengthToDays(tenancyLengthValueMin), 'days'),
      moment(moveIn).add(this.getTenancyLengthToDays(tenancyLengthValueMax) - 1, 'days'),
      moveIn,
    );
  };

  // get move out disable date range
  getMoveOutRange = value => {
    if (!value) {
      return false;
    }
    if (this.state.moveOutDateRange.startValue && this.state.moveOutDateRange.endValue) {
      return (
        this.state.moveOutDateRange.startValue.valueOf() > value.valueOf() ||
        this.state.moveOutDateRange.endValue.valueOf() < value.valueOf()
      );
    } else if (this.state.moveOutDateRange.startValue) {
      return this.state.moveOutDateRange.startValue.valueOf() > value.valueOf();
    } else if (this.state.moveOutDateRange.endValue) {
      return this.state.moveOutDateRange.endValue.valueOf() > value.valueOf();
    }

    return false;
  };

  getTenancyLengthToDays = tenancyLengthValue => {
    let days = 0;
    switch (this.props.billingCycle) {
      case 'MONTHLY':
        days = tenancyLengthValue * 28;
        break;
      default:
        days = tenancyLengthValue * 7;
    }

    return days;
  };

  getMoveInDateContainer = () => this.moveInDateContainer;

  handleMoveOutSelectChange = value => {
    if (value === 'ANYTIME') {
      this.props.form.resetFields('moveOut');
    }
  };
  handleOnChangeTurnaround = event => {
    this.props.form.setFieldsValue({ turnaroundTime: event < 1 ? 1 : parseInt(String(event)) });
  };

  componentDidMount() {
    if (this.props.type === 'edit') {
      let moveInType = this.props.listing.moveInType;
      let moveIn = this.props.listing.moveIn;
      let tenancyLengthType = this.props.listing.tenancyLengthType;
      let tenancyLengthValueMin = this.props.listing.tenancyLengthValue?.split('-')?.[0];
      let tenancyLengthValueMax;
      if (tenancyLengthType === 'BETWEEN') {
        tenancyLengthValueMax = this.props.listing.tenancyLengthValue?.split('-')?.[1];
      }

      switch (moveInType) {
        case 'EXACTLY_MATCH':
          if (tenancyLengthType === 'EQUALS') {
            this.setMoveInExactlyMatchEqual(moveIn, tenancyLengthValueMin, false);
          }
          if (tenancyLengthType === 'NO_LESS_THAN') {
            this.setMoveInExactlyMatchBigger(moveIn, tenancyLengthValueMin);
          }
          if (tenancyLengthType === 'NO_MORE_THAN') {
            this.setMoveInExactlyMatchSmaller(moveIn, tenancyLengthValueMin);
          }
          if (tenancyLengthType === 'BETWEEN') {
            this.setMoveInExactlyMatchBetween(moveIn, tenancyLengthValueMin, tenancyLengthValueMax);
          }
          if (tenancyLengthType === 'NOT_SPECIFIC') {
            this.setMoveInAfterSmaller(moveIn);
          }
          break;
        case 'AFTER':
          if (['NO_LESS_THAN', 'EQUALS'].indexOf(tenancyLengthType) !== -1) {
            this.setMoveInExactlyMatchBigger(moveIn, tenancyLengthValueMin);
          }
          if (tenancyLengthType === 'NOT_SPECIFIC') {
            this.setMoveInAfterSmaller(moveIn);
          }
          if (tenancyLengthType === 'NO_MORE_THAN') {
            this.setMoveInAfterNoMoreThen(moveIn, tenancyLengthValueMin);
          }
          if (tenancyLengthType === 'BETWEEN') {
            this.setMoveInAfterBetween(moveIn, tenancyLengthValueMin);
          }
          break;
        case 'ANYTIME':
          if (['NO_LESS_THAN', 'EQUALS'].indexOf(tenancyLengthType) !== -1) {
            this.setMoveInExactlyMatchBigger(moment(), tenancyLengthValueMin);
          }
          if (tenancyLengthType === 'NOT_SPECIFIC') {
            this.setMoveInAfterSmaller(moment());
          }
          if (tenancyLengthType === 'NO_MORE_THAN') {
            this.setMoveInAnytimeNoMoreThen();
          }
          if (tenancyLengthType === 'BETWEEN') {
            this.setMoveInAnytimeBetween(tenancyLengthValueMin);
          }
          break;
        default:
          break;
      }
    }
  }

  componentDidUpdate() {
    const { resetFields, getFieldValue } = this.props.form;
    if (getFieldValue('turnaroundType') !== 'FIXED_DAYS') {
      resetFields('turnaroundTime');
    }
  }

  render() {
    const { getFieldDecorator, getFieldValue } = this.props.form;
    const isFixedDays = getFieldValue('turnaroundType') === 'FIXED_DAYS';
    const fieldsConfig = getTenancyFieldsConfig(this.props.showPlaceHolder, this.props.isManual);

    return (
      <div className="listing-modal__tenancy-details-container">
        <If condition={!this.props.showTitle}>
          <div className="listing-modal__title isting-modal__title--tenancy-details">
            {this.props.t('cms.listing.modal.tenancy_details.title')}
          </div>
        </If>
        <div className="listing-modal__tenancy-details-input-container">
          {/* Move in */}
          <div className="listing-modal__move-in-container">
            <Row type="flex" justify="space-between">
              <Col>
                <label
                  className={classNames('listing-modal__label listing-modal__label--move-in', {
                    'listing-modal__label--required':
                      !this.props.showTitle && fieldsConfig.moveIn.required,
                  })}
                >
                  {this.props.t('cms.listing.modal.move_in.label')}
                </label>
              </Col>
              <If condition={this.props.isInstantBook}>
                <Col>
                  <Form.Item>
                    {getFieldDecorator('availableThoughMoveInDatePassed', {
                      valuePropName: 'checked',
                    })(
                      <Checkbox
                        disabled={
                          this.props.showPlaceHolder ||
                          getFieldValue('moveInType') !== 'EXACTLY_MATCH' ||
                          getFieldValue('moveOutType') !== 'EXACTLY_MATCH'
                        }
                      >
                        {this.props.t(
                          'cms.listing.modal.available_though_move_In_date_passed.label',
                        )}
                      </Checkbox>,
                    )}
                    <Tooltip
                      title={this.props.t(
                        'cms.listing.modal.available_though_move_In_date_passed.tooltip',
                      )}
                    >
                      <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </Tooltip>
                  </Form.Item>
                </Col>
              </If>
            </Row>
            <div className="listing-modal__move-in-input-container">
              <Row gutter={9}>
                <Col span={8}>
                  <Form.Item className="listing-modal__type">
                    <div
                      className="listing-modal__move-in-type-container"
                      ref={node => {
                        this.moveInTypeContainer = node;
                      }}
                    >
                      {getFieldDecorator('moveInType', {
                        initialValue: fieldsConfig.moveInType.required
                          ? this.props.listing?.moveInType
                          : null,
                        rules: [
                          {
                            required: fieldsConfig.moveInType.required,
                            message: this.props.t(
                              'cms.listing.modal.error_message.date_range_required',
                            ),
                          },
                        ],
                      })(
                        <Select
                          style={{ width: '100%', height: '32px', borderRadius: '2px' }}
                          onSelect={this.handleMoveInSelectChange}
                          getPopupContainer={() => this.moveInTypeContainer}
                          disabled={fieldsConfig.moveInType.disabled}
                          placeholder={this.props.t(
                            'cms.listing.modal.tenancy_details.place_holder',
                          )}
                        >
                          <Option key="EXACTLY_MATCH" value="EXACTLY_MATCH">
                            {this.props.t('cms.listing.modal.move_in.options.exactly_match')}
                          </Option>
                          <Option key="AFTER" value="AFTER">
                            {this.props.t('cms.listing.modal.move_in.options.after')}
                          </Option>
                          <Option key="ANYTIME" value="ANYTIME">
                            {this.props.t('cms.listing.modal.move_in.options.anytime')}
                          </Option>
                        </Select>,
                      )}
                    </div>
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item className="listing-modal__date">
                    <div
                      className="listing-modal__move-in-date-container"
                      ref={node => {
                        this.moveInDateContainer = node;
                      }}
                    >
                      {getFieldDecorator('moveIn', {
                        rules: [
                          {
                            required:
                              fieldsConfig.moveIn.required &&
                              this.props.form.getFieldValue('moveInType') !== 'ANYTIME',
                            message: this.props.t('cms.listing.modal.error_message.can_not_empty'),
                          },
                        ],
                      })(
                        <DatePicker
                          style={{ width: '100%', height: '32px', borderRadius: '2px' }}
                          format="ll"
                          onChange={this.handleMoveInDatePicker}
                          getCalendarContainer={() => this.moveInDateContainer}
                          disabled={
                            this.props.form.getFieldValue('moveInType') === 'ANYTIME' ||
                            fieldsConfig.moveIn.disabled
                          }
                        />,
                      )}
                    </div>
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </div>

          {/* Tenancy length */}
          <div className="listing-modal__tenancy-length-container">
            <label
              className={classNames('listing-modal__label listing-modal__label--tenancy-length', {
                'listing-modal__label--required':
                  !this.props.showTitle && fieldsConfig.tenancyLengthType.required,
              })}
            >
              {this.props.t('cms.listing.modal.tenancy_length.label')}
              <span className="listing-modal__label-billing-cycle">
                {' '}
                (
                {this.props.t(
                  `cms.listing.billing_cycle.per_${
                    this.props.billingCycle ? this.props.billingCycle.toLowerCase() : 'weekly'
                  }`,
                )}
                )
              </span>
            </label>
            <div className="listing-modal__tenancy-length-input-container">
              <Row gutter={9}>
                <Col span={8}>
                  <Form.Item className="listing-modal__type">
                    <div
                      className="listing-modal__tenancy-length-type-container"
                      ref={node => {
                        this.tenancyLengthTypeContainer = node;
                      }}
                    >
                      {getFieldDecorator('tenancyLengthType', {
                        rules: [
                          {
                            required: fieldsConfig.tenancyLengthType.required,
                            message: this.props.t(
                              'cms.listing.modal.error_message.date_range_required',
                            ),
                          },
                        ],
                      })(
                        <Select
                          style={{ height: '32px', borderRadius: '2px' }}
                          onSelect={this.handleTenancyLengthSelectChange}
                          placeholder={this.props.t(
                            'cms.listing.modal.tenancy_details.place_holder',
                          )}
                          disabled={fieldsConfig.tenancyLengthType.disabled}
                          getPopupContainer={() => this.tenancyLengthTypeContainer}
                        >
                          <Option key="EQUALS" value="EQUALS">
                            {this.props.t('cms.listing.modal.tenancy_length.options.equals')}
                          </Option>
                          <Option key="NO_LESS_THAN" value="NO_LESS_THAN">
                            {this.props.t('cms.listing.modal.tenancy_length.options.no_less_than')}
                          </Option>
                          <Option key="NO_MORE_THAN" value="NO_MORE_THAN">
                            {this.props.t('cms.listing.modal.tenancy_length.options.no_more_than')}
                          </Option>
                          <Option key="BETWEEN" value="BETWEEN">
                            {this.props.t('cms.listing.modal.tenancy_length.options.between')}
                          </Option>
                          <Option key="NOT_SPECIFIC" value="NOT_SPECIFIC">
                            {this.props.t('cms.listing.modal.tenancy_length.options.no_specific')}
                          </Option>
                        </Select>,
                      )}
                    </div>
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Row gutter={1}>
                    <Col span={11}>
                      <Form.Item className="listing-modal__type listing-modal__type--tenancy-length-input">
                        {getFieldDecorator('tenancyLengthValueMin', {
                          rules: [
                            {
                              required:
                                fieldsConfig.tenancyLengthValueMin.required &&
                                this.props.form.getFieldValue('tenancyLengthType') !==
                                  'NOT_SPECIFIC',
                              message: this.props.t(
                                'cms.listing.modal.error_message.valid_range_required',
                              ),
                            },
                          ],
                        })(
                          <InputNumber
                            min={1}
                            onChange={this.handleTenancyLengthMinInputChange}
                            precision={0}
                            disabled={
                              this.props.form.getFieldValue('tenancyLengthType') ===
                                'NOT_SPECIFIC' || fieldsConfig.tenancyLengthValueMin.disabled
                            }
                          />,
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <div className="listing-modal__type listing-modal__type--tilde">—</div>
                    </Col>
                    <Col span={11}>
                      <Form.Item className="listing-modal__type listing-modal__type--tenancy-length-input">
                        {getFieldDecorator('tenancyLengthValueMax', {
                          rules: [
                            {
                              required:
                                this.props.form.getFieldValue('tenancyLengthType') === 'BETWEEN' &&
                                fieldsConfig.tenancyLengthValueMax.required &&
                                this.props.form.getFieldValue('moveInType'),
                              message: this.props.t(
                                'cms.listing.modal.error_message.valid_range_required',
                              ),
                            },
                          ],
                        })(
                          <InputNumber
                            onChange={this.handleTenancyLengthMaxInputChange}
                            disabled={
                              this.props.form.getFieldValue('tenancyLengthType') !== 'BETWEEN' ||
                              this.props.form.getFieldValue('tenancyLengthType') === 'NOT_SPECIFIC'
                            }
                            min={
                              this.props.form.getFieldValue('tenancyLengthValueMin')
                                ? Number(this.props.form.getFieldValue('tenancyLengthValueMin')) + 1
                                : 2
                            }
                            precision={0}
                          />,
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          </div>

          {/* Move out */}
          <div className="listing-modal__move-out-container">
            <label
              className={classNames('listing-modal__label listing-modal__label--move-out', {
                'listing-modal__label--required':
                  !this.props.showTitle && fieldsConfig.moveOutType.required,
              })}
            >
              {this.props.t('cms.listing.modal.move_out.label')}
            </label>
            <div className="listing-modal__move-out-input-container">
              <Row gutter={9}>
                <Col span={8}>
                  <Form.Item className="listing-modal__type">
                    <div
                      className="listing-modal__move-out-type-container"
                      ref={node => {
                        this.moveOutTypeContainer = node;
                      }}
                    >
                      {getFieldDecorator('moveOutType', {
                        rules: [
                          {
                            required:
                              fieldsConfig.moveOutType.required &&
                              this.props.form.getFieldValue('moveInType'),
                            message: this.props.t(
                              'cms.listing.modal.error_message.date_range_required',
                            ),
                          },
                        ],
                      })(
                        <Select
                          style={{ height: '32px', borderRadius: '2px' }}
                          onSelect={this.handleMoveOutSelectChange}
                          getPopupContainer={() => this.moveOutTypeContainer}
                          placeholder={this.props.t(
                            'cms.listing.modal.tenancy_details.place_holder',
                          )}
                          disabled={
                            !this.props.form.getFieldValue('moveInType') ||
                            !this.props.form.getFieldValue('tenancyLengthType')
                          }
                        >
                          <Option
                            key="EXACTLY_MATCH"
                            value="EXACTLY_MATCH"
                            disabled={
                              this.props.form.getFieldValue('moveInType') !== 'EXACTLY_MATCH' ||
                              this.props.form.getFieldValue('tenancyLengthType') !== 'EQUALS'
                            }
                          >
                            {this.props.t('cms.listing.modal.move_in.options.exactly_match')}
                          </Option>
                          <Option
                            key="BEFORE"
                            value="BEFORE"
                            disabled={
                              this.props.form.getFieldValue('moveInType') === 'EXACTLY_MATCH' &&
                              ['EQUALS', 'BETWEEN'].includes(
                                this.props.form.getFieldValue('tenancyLengthType'),
                              )
                            }
                          >
                            {this.props.t('cms.listing.modal.move_out.options.before')}
                          </Option>
                          <Option
                            key="ANYTIME"
                            value="ANYTIME"
                            disabled={
                              this.props.form.getFieldValue('moveInType') === 'EXACTLY_MATCH' &&
                              this.props.form.getFieldValue('tenancyLengthType') === 'EQUALS'
                            }
                          >
                            {this.props.t('cms.listing.modal.move_in.options.anytime')}
                          </Option>
                        </Select>,
                      )}
                    </div>
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item className="listing-modal__date listing-modal__col-65">
                    <div
                      className="listing-modal__move-out-date-container"
                      ref={node => {
                        this.moveOutDateContainer = node;
                      }}
                    >
                      {getFieldDecorator('moveOut', {
                        rules: [
                          {
                            required:
                              fieldsConfig.moveOut.required &&
                              this.props.form.getFieldValue('moveOutType') !== 'ANYTIME' &&
                              this.props.form.getFieldValue('moveInType'),
                            message: this.props.t('cms.listing.modal.error_message.can_not_empty'),
                          },
                          {
                            message: this.props.t(
                              'cms.listing.modal.error_message.move_out_after_current_date',
                            ),
                            validator: (rule, value, callback) => {
                              if (
                                moment().isAfter(value) &&
                                !this.props.form.getFieldValue('placeHolder')
                              ) {
                                callback('error');
                              }
                              callback();
                            },
                          },
                        ],
                      })(
                        <DatePicker
                          style={{ width: '100%', height: '32px', borderRadius: '2px' }}
                          getCalendarContainer={() => this.moveOutDateContainer}
                          format="ll"
                          showToday={false}
                          defaultPickerValue={this.state.moveOutDateRange.startValue}
                          disabledDate={value => this.getMoveOutRange(value)}
                          disabled={
                            this.props.form.getFieldValue('moveOutType') === 'ANYTIME' ||
                            !this.props.form.getFieldValue('moveInType') ||
                            !this.props.form.getFieldValue('tenancyLengthType')
                          }
                        />,
                      )}
                    </div>
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </div>

          <TenancyDetailsPreview
            t={this.props.t}
            moveIn={this.props.form.getFieldValue('moveIn')}
            moveOut={this.props.form.getFieldValue('moveOut')}
            tenancyValueMin={this.props.form.getFieldValue('tenancyLengthValueMin')}
            tenancyValueMax={this.props.form.getFieldValue('tenancyLengthValueMax')}
            moveInType={this.props.form.getFieldValue('moveInType')}
            moveOutType={this.props.form.getFieldValue('moveOutType')}
            tenancyType={this.props.form.getFieldValue('tenancyLengthType')}
            billingCycle={this.props.billingCycle}
            showPlaceHolder={this.props.showPlaceHolder}
            showTitle={this.props.showTitle ? this.props.showTitle : false}
          />

          {/* Turnaround */}
          <If condition={this.props.isInstantBook}>
            <div className="listing-modal__move-out-container">
              <label
                className={classNames('listing-modal__label listing-modal__label--move-out', {
                  'listing-modal__label--required': !this.props.showTitle,
                })}
              >
                {this.props.t('cms.listing.modal.turnaround.label')}
                <Tooltip
                  title={this.props.t(
                    'Turnaround is for the period that is needed to prepare the room for the next tenant.',
                  )}
                >
                  <QuestionCircleOutlined
                    style={{ fontSize: 12, color: 'rgba(0,0,0,.45)', marginLeft: 8 }}
                  />
                </Tooltip>
              </label>
              <div className="listing-modal__move-out-input-container">
                <Row gutter={9}>
                  <Col span={8}>
                    <Form.Item className="listing-modal__type">
                      <div className="listing-modal__move-out-type-container">
                        {getFieldDecorator('turnaroundType', {
                          initialValue: this.props.listing?.turnaroundType ?? null,
                          rules: [
                            {
                              required: true,
                              message: this.props.t('This field cannot be empty'),
                            },
                          ],
                        })(
                          <Select
                            style={{ height: '32px', borderRadius: '2px' }}
                            placeholder={this.props.t(
                              'cms.listing.modal.tenancy_details.place_holder',
                            )}
                          >
                            <Option key="NOT_APPLIED" value="NOT_APPLIED">
                              Not applied
                            </Option>
                            <Option key="FIXED_DAYS" value="FIXED_DAYS">
                              Fixed days
                            </Option>
                          </Select>,
                        )}
                      </div>
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <Form.Item className="listing-modal__date listing-modal__col-65">
                      <div className="listing-modal__move-out-date-container">
                        {getFieldDecorator('turnaroundTime', {
                          initialValue: isFixedDays ? this.props.listing?.turnaroundTime : null,
                          rules: [
                            {
                              required: isFixedDays,
                              message: this.props.t('This field cannot be empty'),
                            },
                          ],
                        })(
                          isFixedDays ? (
                            <>
                              <InputNumber
                                onChange={this.handleOnChangeTurnaround}
                                disabled={!isFixedDays}
                                step={1}
                                min={1}
                                value={getFieldValue('turnaroundTime')}
                                formatter={value => {
                                  if (value) {
                                    return value < 1 ? 1 : parseInt(String(value));
                                  }
                                }}
                                style={{ width: '100%', height: '32px', borderRadius: '2px' }}
                              />
                              <div
                                style={{
                                  zIndex: 10,
                                  height: 30,
                                  position: 'absolute',
                                  top: 1,
                                  right: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  paddingLeft: 12,
                                  paddingRight: 12,
                                  backgroundColor: '#fafafa',
                                  borderLeft: '1px solid #d9d9d9',
                                }}
                              >
                                days
                              </div>
                            </>
                          ) : (
                            <Input disabled />
                          ),
                        )}
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          </If>
        </div>
      </div>
    );
  }
}

TenancyDetails.propTypes = {
  t: PropTypes.func.isRequired,
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    resetFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
  billingCycle: PropTypes.string.isRequired,
  showPlaceHolder: PropTypes.bool,
  showTitle: PropTypes.bool,
  isInstantBook: PropTypes.bool,
  type: PropTypes.string,
};

TenancyDetails.defaultProps = {
  t: () => {},
  showPlaceHolder: false,
  showTitle: false,
  isInstantBook: false,
  type: 'create',
};
